import React, {
  useRef,
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from "react";
import { showNotification } from "@mantine/notifications";
import {
  DocumentEditorContainerComponent,
  Toolbar,
  Search,
} from "@syncfusion/ej2-react-documenteditor";
import {
  ActionIcon,
  Button,
  Modal,
  TextInput,
  Paper,
  Checkbox,
  Group,
  Title,
  Text,
  Badge,
  Stack,
} from "@mantine/core";
import constants from "../utilities/constants";
import { getDocsToken } from "../utilities/apis/agreements";
import { apiWrapWithErrorWithData } from "../utilities/apiHelpers";
import { IconTrash, IconPlus } from "@tabler/icons-react";
import { useSelector } from "react-redux";
import { selectUserData } from "../redux/selectors";
DocumentEditorContainerComponent.Inject(Toolbar, Search);
const Editor = forwardRef(
  (
    {
      initialContent,
      placeholders = [],
      setPlaceholders = () => {},
      disabled = false,
      editNotAllowed = false,
    },
    ref,
  ) => {
    console.log("Editor placeholders:", placeholders);
    const [showModal, setShowModal] = useState(false);
    const [phName, setPhName] = useState("");
    const [replaceAll, setReplaceAll] = useState(false); // <-- your "select multiple" checkbox
    const [selectionText, setSelectionText] = useState("");
    const currentUser = useSelector(selectUserData) || {};
    const restrictEdit = currentUser.loginAs.id !== 4 ? true : false;
    const [editorReady, setEditorReady] = useState(false);
    const [currentSelection, setCurrentSelection] = useState("");
    const [activePlaceholder, setActivePlaceholder] = useState(null);

    const [values, setValues] = useState({});

    const hostUrl =
      // "https://wordeditoreapi.truecounsel.in/react/production/api/documenteditor";
      `${constants.wordeditorApi}/api/documenteditor/`;

    const editorRef = useRef(null);

    useImperativeHandle(ref, () => ({
      loadDocument(sfdt) {
        const editor = editorRef.current?.documentEditor;

        if (!editor || !sfdt) return;

        requestAnimationFrame(() => {
          const content =
            typeof sfdt === "string" ? sfdt : JSON.stringify(sfdt);

          editor.open(content);

          editor.isReadOnly = editNotAllowed;

          editor.resize();
          editorRef.current?.resize();
        });
      },

      createBlankDocument() {
        const editor = editorRef.current?.documentEditor;

        if (!editor) return;

        editor.openBlank();

        editor.isReadOnly = editNotAllowed;

        requestAnimationFrame(() => {
          editor.focusIn();
          editor.resize();
          editorRef.current?.resize();
        });
      },

      async saveDocument() {
        const editor = editorRef.current?.documentEditor;

        if (!editor) return null;

        return await editor.saveAsBlob("Docx");
      },

      getSfdt() {
        return editorRef.current?.documentEditor?.serialize() ?? null;
      },
    }));

    useEffect(() => {
      const container = editorRef.current;

      if (!container?.documentEditor) return;

      container.documentEditor.layoutType = "Continuous";
      const readOnly = editNotAllowed;
      container.documentEditor.isReadOnly = readOnly;
    }, [editNotAllowed, restrictEdit]);

    useEffect(() => {
      const wait = () => {
        const container = editorRef.current;

        if (!container?.documentEditor) {
          requestAnimationFrame(wait);
          return;
        }

        setEditorReady(true);
      };

      wait();
    }, []);

    useEffect(() => {
      if (!editorReady || !initialContent) return;

      const editor = editorRef.current?.documentEditor;

      if (!editor) return;

      try {
        let contentToOpen;

        if (typeof initialContent === "string") {
          contentToOpen = initialContent;
        } else {
          contentToOpen = JSON.stringify(initialContent);
        }

        editor.open(contentToOpen);

        editor.isReadOnly = editNotAllowed;
        editor.enableSpellCheck = true;

        requestAnimationFrame(() => {
          editor.focusIn();
          editor.resize();
          editorRef.current?.resize();
        });
      } catch (e) {
        console.error("Failed to open document:", e);
      }
    }, [editorReady, initialContent, editNotAllowed]);

    useEffect(() => {
      return () => {
        const iframe = document.querySelector(".e-de-text-target");

        if (iframe instanceof HTMLElement) {
          iframe.blur();
        }
      };
    }, []);

    useEffect(() => {
      const container = editorRef.current;

      if (!container) return;

      setTimeout(() => {
        container.resize();
        container.documentEditor.resize();
      }, 100);
    }, []);

    const handleSelectionChange = () => {
      const editor = editorRef.current?.documentEditor;
      if (!editor) return;

      const text = editor.selection?.text || "";
      setCurrentSelection(text);
    };

    const createPlaceholder = () => {
      const editor = editorRef.current?.documentEditor;
      if (!editor) return;

      editor.focusIn();

      setTimeout(() => {
        const selection = editor.selection?.text || "";

        if (!selection.trim()) {
          showNotification({
            color: "yellow",
            title: "No Selection",
            message: "Please select some text.",
          });
          return;
        }

        setSelectionText(selection);
        setPhName("");
        setReplaceAll(false);
        setShowModal(true);
      }, 50);
    };

    const confirmCreatePlaceholder = () => {
      const editor = editorRef.current?.documentEditor;
      if (!editor) return;

      const name = phName.trim();
      if (!name) {
        alert("Please enter a placeholder name.");
        return;
      }

      if (replaceAll && selectionText.trim()) {
        const search = editor.search || editor.searchModule;
        if (!search) {
          console.error("Search module not available — did you inject Search?");
        } else {
          if (search.searchResults?.clear) search.searchResults.clear();

          // Exact match (case-sensitive). To make it case-insensitive, pass "CaseInsensitive".
          search.findAll(selectionText /* , "CaseInsensitive" */);

          const results = search.textSearchResults;
          if (results?.innerList?.length > 0) {
            search.searchResults.replaceAll(`[[${name}]]`);
            // Now search for new placeholders and highlight
            search.findAll(`[[${name}]]`);
            const newResults = search.textSearchResults;
            if (newResults?.innerList?.length > 0) {
              newResults.innerList.forEach((r) => {
                editor.selection.select(r);
                editor.selection.characterFormat.highlightColor = "Yellow"; // or "#FFFF00"
              });
            }
          }
        }
      } else {
        editor.selection.characterFormat.highlightColor = "Yellow";
        editor.editor.insertText(`[[${name}]]`, true);
      }

      setPlaceholders((prev) => [...prev, name]);
      setActivePlaceholder(name);
      setShowModal(false);
    };

    // You already had this — updated to rely on textSearchResults
    const replacePlaceholder = (name, value) => {
      const de = editorRef.current?.documentEditor;
      if (!de) return;

      const wasReadOnly = de.isReadOnly;
      if (restrictEdit || editNotAllowed) {
        // Temporarily disable protection to allow placeholder replacement
        de.isReadOnly = false;
      }
      const needle = `[[${name}]]`;
      const search = de.search || de.searchModule;

      if (!search) {
        console.error("Search module not available — did you inject Search?");
        return;
      }

      if (search.searchResults?.clear) search.searchResults.clear();

      search.findAll(needle /* , "CaseInsensitive" */);
      const results = search.textSearchResults;

      if (results && results.innerList && results.innerList.length > 0) {
        de.selection.characterFormat.highlightColor = "transparent";
        search.searchResults.replaceAll(String(value ?? ""));
        // setValue("");
        // de.focusIn();

        // Restore read-only state if it was originally read-only
        if (wasReadOnly) {
          de.isReadOnly = true;
        }
        console.log(`Replaced ${needle} with "${value}"`);
      } else {
        console.log(`No matches found for ${needle}`);
        // Restore read-only state even if no matches found
        if (wasReadOnly) {
          de.isReadOnly = true;
        }
      }
    };

    const handleAddClick = ({ name, value }) => {
      setActivePlaceholder(name);

      replacePlaceholder(name, value);
    };

    const movePlaceholder = (fromIndex, toIndex) => {
      if (toIndex < 0 || toIndex >= placeholders.length) return; // out of bounds
      const updated = [...placeholders];
      const [movedItem] = updated.splice(fromIndex, 1);
      updated.splice(toIndex, 0, movedItem);
      setPlaceholders(updated);
    };
    return (
      <div
        style={{
          width: "100%",
        }}
      >
        {/* <div className="flex justify-end mb-4">
          {!(disabled || editNotAllowed) && (
            <Button
              radius="md"
              leftSection={<IconPlus size={16} />}
              onClick={createPlaceholder}
            >
              Create Placeholder{" "}
            </Button>
          )}
        </div> */}
        {/* <Button onClick={getSfdtContent}>get content</Button> */}
        <div
          className="flex gap-6 w-full"
          style={{
            alignItems: "flex-start",
          }}
        >
          <div
            style={{
              flex: 1,
              minWidth: 0,
              height: "780px",
              border: "1px solid #E5E7EB",
              borderRadius: 12,
              overflow: "hidden",
              background: "#fff",
            }}
          >
            <DocumentEditorContainerComponent
              id="container"
              ref={editorRef}
              height="100%"
              width="100%"
              style={{
                height: "100%",
                width: "100%",
              }}
              enableToolbar={true}
              toolbarMode="Scrollable"
              toolbarItems={
                editNotAllowed
                  ? ["Find"]
                  : [
                      "Open",
                      "Separator",
                      "Undo",
                      "Redo",
                      "Separator",
                      "Image",
                      "Table",
                      "Hyperlink",
                      "Separator",
                      "Header",
                      "Footer",
                      "PageSetup",
                      "PageNumber",
                      "Separator",
                      "Break",
                      "Find",
                    ]
              }
              serviceUrl={hostUrl}
              showPropertiesPane={false}
              selectionChange={handleSelectionChange}
            />
          </div>

          {!editNotAllowed && (
            <Paper
              withBorder
              radius="xl"
              shadow="sm"
              p="lg"
              style={{
                width: 360,
                flexShrink: 0,
                height: "fit-content",
                position: "sticky",
                top: 80,
              }}
            >
              <div
                id="placeholders"
                style={{
                  width: 320,
                  flexShrink: 0,
                }}
                className="flex flex-col gap-4"
              >
                <Group justify="space-between" mb="sm">
                  <div>
                    <Title order={5}>Placeholders</Title>
                  </div>

                  <Badge size="lg" radius="xl" variant="light">
                    {placeholders.length} Fields
                  </Badge>
                </Group>
                {!(disabled || editNotAllowed) && (
                  <Button
                    fullWidth
                    radius="md"
                    leftSection={<IconPlus size={16} />}
                    onClick={createPlaceholder}
                  >
                    Add New Placeholder
                  </Button>
                )}

                {placeholders.length === 0 ? (
                  <Paper withBorder radius="lg" p="xl" bg="#fafafa">
                    <div className="text-center py-8">
                      {!(disabled || editNotAllowed) && (
                        <IconPlus
                          size={42}
                          color="#0F7EA5"
                          style={{ marginBottom: 16 }}
                        />
                      )}
                      <Title order={5}>No placeholders yet</Title>

                      {!(disabled || editNotAllowed) && (
                        <Text size="sm" c="dimmed" mt={8}>
                          Select any text in the document and click
                          <br />
                          <b>Add New Placeholder</b>.
                        </Text>
                      )}
                    </div>
                  </Paper>
                ) : (
                  placeholders &&
                  placeholders.length > 0 &&
                  placeholders.map((pl, index) => (
                    <Paper
                      withBorder
                      radius="lg"
                      p="md"
                      shadow="xs"
                      key={index}
                      onClick={() => setActivePlaceholder(pl)}
                      style={{
                        cursor: "pointer",
                        border:
                          activePlaceholder === pl
                            ? "2px solid #0F7EA5"
                            : "1px solid #E5E7EB",

                        background:
                          activePlaceholder === pl ? "#F5FBFE" : "#FFFFFF",

                        boxShadow:
                          activePlaceholder === pl
                            ? "0 8px 20px rgba(15,126,165,.15)"
                            : "0 2px 8px rgba(0,0,0,.05)",

                        transition: "all .25s ease",
                      }}
                    >
                      <div className=" items-center">
                        <Stack gap="sm">
                          <TextInput
                            label={pl}
                            placeholder={`Enter ${pl}`}
                            value={values[pl] || ""}
                            onChange={(e) =>
                              setValues((prev) => ({
                                ...prev,
                                [pl]: e.target.value,
                              }))
                            }
                            className="flex-1"
                            rightSection={
                              values[pl] ? <Badge color="green">✓</Badge> : null
                            }
                            onFocus={() => setActivePlaceholder(pl)}
                          />
                          <div className="flex justify-between mt-2">
                            {disabled ? (
                              <div></div>
                            ) : (
                              <div className="flex gap-2">
                                <Group gap={6}>
                                  <ActionIcon
                                    variant="outline"
                                    onClick={() =>
                                      movePlaceholder(index, index - 1)
                                    }
                                    disabled={index === 0}
                                  >
                                    ↑
                                  </ActionIcon>

                                  <ActionIcon
                                    variant="outline"
                                    onClick={() =>
                                      movePlaceholder(index, index + 1)
                                    }
                                    disabled={index === placeholders.length - 1}
                                  >
                                    ↓
                                  </ActionIcon>
                                </Group>
                              </div>
                            )}
                            {!disabled ? (
                              <ActionIcon
                                onClick={() => {
                                  const updated = placeholders.filter(
                                    (_, i) => i !== index,
                                  );
                                  setPlaceholders(updated);
                                }}
                              >
                                <IconTrash
                                  size={18}
                                  color="red"
                                  variant="light"
                                  radius="xl"
                                />
                              </ActionIcon>
                            ) : (
                              <Button
                                onClick={() =>
                                  handleAddClick({
                                    name: pl,
                                    value: values[pl],
                                  })
                                }
                                fullWidth
                                radius="md"
                                mt="sm"
                              >
                                Apply
                              </Button>
                            )}
                          </div>
                        </Stack>
                      </div>
                    </Paper>
                  ))
                )}
              </div>
            </Paper>
          )}
        </div>

        <Modal
          opened={showModal}
          onClose={() => setShowModal(false)}
          title="Create Placeholder"
          centered
          size="md"
        >
          <TextInput
            label="Selected Text"
            value={selectionText}
            readOnly
            mb="md"
          />

          <TextInput
            label="Placeholder Name"
            value={phName}
            onChange={(e) => setPhName(e.target.value)}
            placeholder="CustomerName"
            mb="md"
          />

          <Checkbox
            label="Replace all occurrences in document"
            checked={replaceAll}
            onChange={(e) => setReplaceAll(e.currentTarget.checked)}
            mb="lg"
          />

          <Group justify="flex-end">
            <Button variant="default" onClick={() => setShowModal(false)}>
              Cancel
            </Button>

            <Button onClick={confirmCreatePlaceholder}>
              Create Placeholder
            </Button>
          </Group>
        </Modal>
      </div>
    );
  },
);

export default Editor;
